import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import {
  AssignAiSystemToUserRequest,
  assignAiSystemToUserWithoutInvitation,
  deleteUser,
  deleteUserFromAccount,
  deleteUserFromSingleAiSystem,
  getAiSystemsForUser,
  getTeamRoles,
  getTeamUsers,
  getUsers,
  revokeInvitation,
  resendInvitation,
  updatePlatformRole,
  updateUserRoleInAiSystem
} from '../../services/TeamService';
import {
  appendUsersSuccess,
  assignAiSystemWithoutInvitationFailure,
  assignAiSystemWithoutInvitationStart,
  fetchAiSystemsForUserError,
  fetchAiSystemsForUserStart,
  fetchAiSystemsForUserSuccess,
  fetchDeleteUserFromAccountFailure,
  fetchDeleteUserFromAccountStart,
  fetchDeleteUserFromAccountSuccess,
  fetchUsersFailure,
  fetchUsersStart,
  fetchUsersSuccess,
  fetchTeamRolesFailure,
  fetchTeamRolesStart,
  fetchTeamRolesSuccess,
  fetchTeamUsersFailure,
  fetchTeamUsersStart,
  fetchTeamUsersSuccess,
  removedLastSystemPerUserFailure,
  removedLastSystemPerUserStart,
  removedLastSystemPerUserSuccess,
  resendInvitationFailure,
  resendInvitationStart,
  resendInvitationSuccess,
  setAssignAiSystemWithoutInvitationSuccess,
  setIsRolePerAiSystemChangedError,
  setIsRolePerAiSystemChangedStart,
  setIsRolePerAiSystemChangedSuccess,
  setRemoveUserFromSingleAiSystemError,
  setRemoveUserFromSingleAiSystemLoading,
  setRemoveUserFromSingleAiSystemSuccess
} from '../../store/slices/userManagementSlice';
import {
  AssignablePlatformRole,
  TeamRolesDTO,
  UpdateUserRoleRequest,
  USERS_SORT_KEYS,
  UsersSortBy
} from '../../types/Team.types';
import { getApiErrorMessage } from '../../utils/apiError';

const isUsersSortBy = (value: string): value is UsersSortBy =>
  (USERS_SORT_KEYS as string[]).includes(value);

export function useUserManagement() {
  const dispatch = useAppDispatch();
  const currentUserEmail = useAppSelector((state) => state.auth.user?.email);
  const { usersFilters, usersPagination, sorting } = useAppSelector(
    (state) => state.userManagement
  );

  /**
   * The whole user directory in one request, scoped server-side from the token.
   * No accountId is involved, which is what makes this work for a superadmin.
   *
   * `append` accumulates the next page for load-more instead of replacing.
   */
  const fetchUsers = async (append = false) => {
    dispatch(fetchUsersStart());
    try {
      const response = await getUsers({
        ...(usersFilters.q ? { q: usersFilters.q } : {}),
        type: usersFilters.type,
        ...(usersFilters.organizationId.length
          ? { organizationId: usersFilters.organizationId }
          : {}),
        // An unrecognised sortBy is a 400, so only forward a key the server
        // documents; an empty one lets it apply its own default.
        ...(sorting.sortBy && isUsersSortBy(sorting.sortBy)
          ? { sortBy: sorting.sortBy, order: sorting.sortOrder }
          : {}),
        limit: usersPagination.limit,
        offset: append ? usersPagination.offset : 0
      });

      dispatch(
        append
          ? appendUsersSuccess(response.data)
          : fetchUsersSuccess(response.data)
      );
    } catch (error) {
      if (error instanceof Error) {
        dispatch(fetchUsersFailure(error.message));
      }
    }
  };

  const fetchTeamUsers = async (accountId: string) => {
    dispatch(fetchTeamUsersStart());
    try {
      const response = await getTeamUsers(accountId);
      const filteredUsers = currentUserEmail
        ? response.data.filter(
            (user) =>
              user.email.trim().toLowerCase() !==
              currentUserEmail.trim().toLowerCase()
          )
        : response.data;

      dispatch(fetchTeamUsersSuccess(filteredUsers));
    } catch (error) {
      if (error instanceof Error) {
        dispatch(fetchTeamUsersFailure(error.message));
      }
    }
  };

  const fetchTeamRoles = async () => {
    dispatch(fetchTeamRolesStart());
    try {
      const response = await getTeamRoles();
      dispatch(fetchTeamRolesSuccess(response.data as TeamRolesDTO));
    } catch (error) {
      if (error instanceof Error) {
        dispatch(fetchTeamRolesFailure(error.message));
      }
    }
  };

  const fetchAiSystemsForUser = async (userId: string, query?: string) => {
    dispatch(fetchAiSystemsForUserStart(true));
    try {
      const response = await getAiSystemsForUser(userId, query);
      dispatch(fetchAiSystemsForUserSuccess(response.data));
    } catch (error) {
      if (error instanceof Error) {
        dispatch(fetchAiSystemsForUserError(error.message));
      }
    }
  };

  const sendRoleChangesPerAiSystem = async (
    userId: string,
    bodyRequest: UpdateUserRoleRequest
  ) => {
    dispatch(setIsRolePerAiSystemChangedStart(true));
    try {
      await updateUserRoleInAiSystem(userId, bodyRequest);

      dispatch(setIsRolePerAiSystemChangedSuccess(true));
    } catch (error) {
      if (error instanceof Error) {
        dispatch(setIsRolePerAiSystemChangedError(error.message));
      }
    }
  };

  const removeUserFromSingleAiSystem = async (
    accountId: string,
    userId: string,
    aiSystemId: string,
    lastAiSystem: boolean = false
  ) => {
    dispatch(setRemoveUserFromSingleAiSystemLoading(true));
    if (lastAiSystem) {
      dispatch(removedLastSystemPerUserStart());
    }
    try {
      await deleteUserFromSingleAiSystem(accountId, userId, aiSystemId);
      dispatch(setRemoveUserFromSingleAiSystemSuccess(true));
      if (lastAiSystem) {
        dispatch(removedLastSystemPerUserSuccess());
      }
    } catch (error) {
      if (error instanceof Error) {
        dispatch(setRemoveUserFromSingleAiSystemError(error.message));
        if (lastAiSystem) {
          dispatch(removedLastSystemPerUserFailure(error.message));
        }
      }
    }
  };

  /**
   * Removes whatever the profile is showing, and RETURNS whether it worked so
   * the caller can decide whether to navigate away. The old void signature is
   * why the profile unmounted before the response arrived and no refusal could
   * ever be rendered.
   *
   * Two endpoints, because the two row types are two different records:
   * DELETE /admin/users/{id} really deletes a member but resolves ids against
   * the User table, so it 404s on an invitation id; DELETE /users/{id} is the
   * one that recognises an invitation and revokes it — and is NOT a deletion
   * for a registered member, which is what this button used to do to everyone.
   */
  const deleteTeamMember = async (
    targetId: string,
    isRegistered: boolean,
    revokeSuperAdminInvitation = false
  ): Promise<boolean> => {
    dispatch(fetchDeleteUserFromAccountStart());
    try {
      await (isRegistered
        ? deleteUser(targetId)
        : revokeSuperAdminInvitation
          ? revokeInvitation(targetId)
          : deleteUserFromAccount(targetId));
      dispatch(fetchDeleteUserFromAccountSuccess(true));
      return true;
    } catch (error) {
      // getApiErrorMessage, not error.message: an axios rejection carries the
      // generic "Request failed with status code 409" and the server's actual
      // sentence lives on response.data. The interceptor has already toasted it
      // — the 400 names the AI systems still owned, CANNOT_DELETE_LAST_ADMIN
      // names the fix — so this only records it.
      dispatch(
        fetchDeleteUserFromAccountFailure(
          getApiErrorMessage(error, 'Failed to delete the user.')
        )
      );
      return false;
    }
  };

  /**
   * Promotes to ADMIN or demotes to USER, returning the server's own account of
   * what happened. Callers must read `changed` before announcing anything.
   *
   * Errors are rethrown rather than swallowed into the store: the profile stays
   * mounted across this mutation and needs the error *code* at the call site to
   * tell an ownership-transfer refusal from everything else.
   */
  const changePlatformRole = async (
    userId: string,
    role: AssignablePlatformRole
  ) => {
    const response = await updatePlatformRole(userId, role);

    // Refetched on both branches. changed:true obviously moved the row, but
    // changed:false is the case that most needs it: the control only offered
    // this action because the list disagreed with the server, so a no-op is
    // proof the mirror is stale and leaving it would keep offering an action
    // the server considers already done.
    //
    // Refetched rather than patched, because the row's platformRole is a mirror
    // and a demotion can move AI system ownership onto rows nobody touched.
    await fetchUsers();

    return response.data;
  };

  const resendInvitationForMember = async (invitationId: string) => {
    dispatch(resendInvitationStart());
    try {
      const response = await resendInvitation(invitationId);
      dispatch(resendInvitationSuccess());
      return response.data.message;
    } catch (error) {
      if (error instanceof Error) {
        dispatch(resendInvitationFailure(error.message));
      }
      return null;
    }
  };

  const assignAiSystemWithoutInvitation = async (
    accountId: string,
    params: AssignAiSystemToUserRequest
  ) => {
    dispatch(assignAiSystemWithoutInvitationStart());
    try {
      await assignAiSystemToUserWithoutInvitation(accountId, params);
      dispatch(setAssignAiSystemWithoutInvitationSuccess(true));
    } catch (error) {
      if (error instanceof Error) {
        dispatch(assignAiSystemWithoutInvitationFailure(error.message));
      }
    }
  };

  return {
    fetchUsers,
    fetchTeamUsers,
    fetchTeamRoles,
    fetchAiSystemsForUser,
    sendRoleChangesPerAiSystem,
    removeUserFromSingleAiSystem,
    deleteTeamMember,
    changePlatformRole,
    resendInvitationForMember,
    assignAiSystemWithoutInvitation
  };
}
